import { useState, useEffect, useRef } from "react";
import { Loader2, Brain, Activity, Clock, Zap } from "lucide-react";
import { KarolStatusHeader } from "./KarolStatusHeader";
import { KarolMetricsCards, fmt } from "./KarolMetricsCards";
import { KarolCustomerBehaviorSection } from "./KarolCustomerBehaviorSection";
import { KarolSalesSignalsSection } from "./KarolSalesSignalsSection";
import { KarolRecommendationsSection } from "./KarolRecommendationsSection";
import { KarolMenuIntelligenceSection } from "./KarolMenuIntelligenceSection";
import { KarolMenuRecommendationsSection } from "./KarolMenuRecommendationsSection";
import { KarolFinanceSection } from "./KarolFinanceSection";
import { KarolFinanceRecommendationsSection } from "./KarolFinanceRecommendationsSection";
import { KarolReliabilitySection } from "./KarolReliabilitySection";
import { KarolReliabilityRecommendationsSection } from "./KarolReliabilityRecommendationsSection";
import { KarolCustomerIdentitySection } from "./KarolCustomerIdentitySection";
import { KarolCustomerIdentityRecommendationsSection } from "./KarolCustomerIdentityRecommendationsSection";

const EVENT_LABELS = {
  order_created:    "Pedido criado",
  order_paid:       "Pedido pago",
  order_cancelled:  "Pedido cancelado",
  cart_abandoned:   "Carrinho abandonado",
  customer_created: "Novo cliente",
  customer_returned:"Cliente regressou",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (mins < 1)  return "agora mesmo";
  if (mins < 60) return `há ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24)    return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
    </div>
  );
}

function DisabledState() {
  return (
    <div className="text-center py-14">
      <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Brain className="w-6 h-6 text-gray-300" />
      </div>
      <p className="text-sm font-medium text-gray-500">Karol não está activa</p>
      <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
        Active <code className="bg-gray-100 px-1 rounded text-[11px]">KAROL_OBSERVER_ENABLED=true</code> para iniciar a observação.
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-14">
      <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Activity className="w-6 h-6 text-gray-300" />
      </div>
      <p className="text-sm font-medium text-gray-500">Sem dados ainda</p>
      <p className="text-xs text-gray-400 mt-1">
        Execute <code className="bg-gray-100 px-1 rounded text-[11px]">POST /karol/insights/rebuild</code> para calcular o perfil.
      </p>
    </div>
  );
}

function ErrorState() {
  return (
    <div className="text-center py-14">
      <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Activity className="w-6 h-6 text-gray-300" />
      </div>
      <p className="text-sm text-gray-400">Dados indisponíveis de momento.</p>
    </div>
  );
}

export function KarolHealthSummary({ authAxios, restaurantId }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [fetchErr, setFetchErr] = useState(false);
  const axiosRef                = useRef(authAxios);
  axiosRef.current              = authAxios;

  useEffect(() => {
    if (!restaurantId) { setLoading(false); return; }
    axiosRef.current
      .get(`/karol/insights/${restaurantId}`)
      .then(r => setData(r.data))
      .catch(() => setFetchErr(true))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  if (loading)                               return <LoadingState />;
  if (fetchErr)                              return <ErrorState />;
  if (!data || data.karol_enabled === false) return <DisabledState />;
  if (!data.initialized)                     return <EmptyState />;

  const eventLabel = EVENT_LABELS[data.last_event_type] ?? data.last_event_type ?? "—";

  return (
    <div className="space-y-4" data-testid="karol-health-summary">

      <KarolStatusHeader karolStatus={data.karol_status} />

      <KarolMetricsCards
        ordersToday={data.orders_today}
        revenueToday={data.revenue_today}
        avgTicketToday={data.avg_ticket_today}
      />

      {/* ── Last event ───────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
        <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
          <Zap className="w-4 h-4 text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Último evento</p>
          <p className="text-sm font-medium text-gray-800 truncate">{eventLabel}</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400 flex-shrink-0">
          <Clock className="w-3 h-3" />
          {timeAgo(data.last_event_at)}
        </div>
      </div>

      <KarolFinanceSection financeSnapshot={data.finance_snapshot} />

      <KarolFinanceRecommendationsSection financeRecommendations={data.finance_recommendations} />

      <KarolReliabilitySection reliabilitySnapshot={data.reliability_snapshot} />

      <KarolReliabilityRecommendationsSection
        reliabilityRecommendations={data.reliability_recommendations}
      />

      <KarolCustomerIdentitySection customerIdentitySnapshot={data.customer_identity_snapshot} />

      <KarolCustomerIdentityRecommendationsSection
        customerIdentityRecommendations={data.customer_identity_recommendations}
      />

      <KarolCustomerBehaviorSection behavior={data.customer_behavior} />

      <KarolSalesSignalsSection signals={data.sales_signals} />

      <KarolMenuIntelligenceSection menuIntelligence={data.menu_intelligence} />

      <KarolMenuRecommendationsSection menuRecommendations={data.menu_recommendations} />

      <KarolRecommendationsSection recommendations={data.recommendations} />

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <p className="text-[11px] text-gray-400 text-center">
        Calculado em {data.rebuilt_at ? new Date(data.rebuilt_at).toLocaleString("pt-PT") : "—"}
        {" · "}{data.total_observed ?? 0} eventos nos últimos {data.window_days ?? 30} dias
      </p>
    </div>
  );
}
